// What changed between two states of one configuration, as a person would read it:
// "Firetube › Flange Position: Centered → Offset".
//
// Kevin, 2026-10-06: "if an edit was made to a configuration by someone who was not the
// creator then it should send an email to the original creator and show what something was
// and what it got changed to, who changed it, and when it got changed." This is the "what";
// util/configChange.ts sends it, api/config-change decides who hears about it.
//
// ONLY WHAT A PERSON ANSWERS. Dropdowns (type 4) and text/size boxes (type 7) the user can
// see. Computed fields (the id key, the description, HOUSING) are hidden by a constant rule
// and change as a CONSEQUENCE of an answer, so listing them would bury the one change that
// mattered under five that merely followed from it. A field shown in EITHER state counts: a
// value that disappeared because its section was hidden is a change the creator would want
// to see.

import { isHidden } from "./rule";
import { stripHtml } from "./text";
import { isManuallyHidden } from "./hiddenQuestions";
import type { CpqData } from "../data/loader";
import type { AnswerState, Question } from "../types";

export type ConfigChange = {
  tab: string;
  field: string;
  from: string;
  to: string;
};

const seq = (v: unknown) => Number(v || 0);

function answerLabel(a: NonNullable<Question["_answers"]>[number]): string {
  return (stripHtml(a.custrecord_cpqc_answer_label_html) || a.name || a.custrecord_cpqc_answer_code).trim();
}

const shown = (v: unknown): string => {
  if (v === undefined || v === null || v === false) return "";
  if (v === true) return "Yes";
  return String(v).trim();
};

/** The value a question shows in `state`, as text. "" when blank. */
function valueOf(q: Question, state: AnswerState): string {
  const code = q.custrecord_cpqc_question_code;
  const answers = q._answers ?? [];
  if (q.custrecord_cpqc_question_type === "4") {
    return answers
      .filter((a) => state[`${code}/${a.custrecord_cpqc_answer_code}`] === true)
      .map(answerLabel)
      .join(", ");
  }
  // Text. A plain box keeps its value under its one answer's slot; a typeable size box
  // (Flange O.D.) lists its sizes as answers and keeps the typed value under CODE/CODE.
  const keys = [`${code}/${code}`, ...answers.map((a) => `${code}/${a.custrecord_cpqc_answer_code}`)];
  const vals: string[] = [];
  for (const k of new Set(keys)) {
    const v = shown(state[k]);
    if (v && !vals.includes(v)) vals.push(v);
  }
  return vals.join(", ");
}

/** Every user-facing field whose value differs between `before` and `after`, in the order
 *  the form shows them. Empty when nothing a person answers has changed. */
export function diffConfigs(data: CpqData, before: AnswerState, after: AnswerState): ConfigChange[] {
  const tabs = new Map(data.tabs.map((t) => [t.id, t]));
  const groups = new Map(
    data.tabs.flatMap((t) => (t._groups ?? []).map((g) => [g.id, g] as const))
  );
  const visibleIn = (q: Question, state: AnswerState): boolean => {
    const tab = tabs.get(q.custrecord_cpqc_question_tab);
    if (tab && isHidden({ hideRule: tab.custrecord_cpqc_tab_hide_rule, parsedHideRule: tab.custrecord_cpqc_tab_parsed_hide_rule }, state))
      return false;
    const g = groups.get(q.custrecord_cpqc_question_group);
    if (g && isHidden({ hideRule: g.custrecord_cpqc_group_hide_rule, parsedHideRule: g.custrecord_cpqc_group_parsed_hide_rule }, state))
      return false;
    return !isHidden({ hideRule: q.custrecord_cpqc_question_hide_rule, parsedHideRule: q.custrecord_cpqc_question_p_hide_rule }, state);
  };

  const qs = data.questions
    .filter((q) => {
      const t = q.custrecord_cpqc_question_type;
      if (t !== "4" && t !== "7") return false;
      const tab = tabs.get(q.custrecord_cpqc_question_tab);
      if (!tab || tab.custrecord_cpqc_tab_code === "DEBUG") return false;
      return !isManuallyHidden(data.slug, q.custrecord_cpqc_question_code);
    })
    .sort((a, b) => {
      const ta = tabs.get(a.custrecord_cpqc_question_tab);
      const tb = tabs.get(b.custrecord_cpqc_question_tab);
      const ga = groups.get(a.custrecord_cpqc_question_group);
      const gb = groups.get(b.custrecord_cpqc_question_group);
      return (
        seq(ta?.custrecord_cpqc_tab_sequence) - seq(tb?.custrecord_cpqc_tab_sequence) ||
        seq(ga?.custrecord_cpqc_group_sequence) - seq(gb?.custrecord_cpqc_group_sequence) ||
        seq(a.custrecord_cpqc_question_sequence) - seq(b.custrecord_cpqc_question_sequence)
      );
    });

  const out: ConfigChange[] = [];
  for (const q of qs) {
    const from = valueOf(q, before);
    const to = valueOf(q, after);
    if (from === to) continue;
    if (!visibleIn(q, before) && !visibleIn(q, after)) continue;
    out.push({
      tab: tabs.get(q.custrecord_cpqc_question_tab)?.name ?? "",
      field: stripHtml(q.custrecord_cpqc_question_label_html) || q.name,
      from,
      to,
    });
  }
  return out;
}
